import axios from "axios";

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5003/api";

export const APP_URL = process.env.REACT_APP_APP_URL || "http://localhost:4003";

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
});

/**
 * Format an integer amount in cents for display, e.g. 5900 -> "$59".
 */
export function formatCents(cents, { withDecimals = false } = {}) {
  const value = (cents || 0) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: withDecimals ? 2 : 0,
    maximumFractionDigits: withDecimals ? 2 : 0,
  }).format(value);
}

/**
 * Hardcoded fallback plans matching the design; used when GET /plans
 * is unreachable. Prices are integer cents.
 */
export const FALLBACK_PLANS = [
  {
    id: "starter",
    name: "Starter",
    priceCents: 38800,
    popular: false,
    description: "Perfect for small businesses just getting started.",
    features: [
      "Up to 2 users",
      "1 company",
      "Unlimited invoices",
      "Expense tracking",
      "Bank reconciliation",
      "Basic reports",
      "Email support",
    ],
  },
  {
    id: "professional",
    name: "Professional",
    priceCents: 74800,
    popular: true,
    description: "Ideal for growing businesses that need more power.",
    features: [
      "Up to 5 users",
      "Up to 4 companies",
      "Unlimited invoices",
      "Expense tracking",
      "Bank reconciliation",
      "Advanced reports",
      "Payroll & 1099",
      "Priority support",
    ],
  },
  {
    id: "business",
    name: "Business",
    priceCents: 94800,
    popular: false,
    description: "Advanced features for established businesses.",
    features: [
      "Unlimited users",
      "Up to 4 companies",
      "Unlimited invoices",
      "Expense tracking",
      "Bank reconciliation",
      "Advanced reports",
      "Payroll & 1099",
      "Custom roles",
      "Priority support",
      "API access",
    ],
  },
];

function failure(error, message) {
  const apiMessage =
    error && error.response && error.response.data
      ? error.response.data.message
      : null;
  return {
    ok: false,
    offline: !error || !error.response,
    code:
      error && error.response && error.response.data
        ? error.response.data.code
        : undefined,
    message: apiMessage || message,
  };
}

export const api = {
  /** GET /plans — falls back to hardcoded plans when unreachable. */
  async getPlans() {
    try {
      const { data } = await client.get("/plans");
      if (data && data.ok && Array.isArray(data.plans) && data.plans.length) {
        return { ok: true, plans: data.plans, fromApi: true };
      }
      return { ok: true, plans: FALLBACK_PLANS, fromApi: false };
    } catch (error) {
      return { ok: true, plans: FALLBACK_PLANS, fromApi: false };
    }
  },

  /** POST /auth/register */
  async register(payload) {
    try {
      const { data } = await client.post("/auth/register", payload);
      return data;
    } catch (error) {
      return failure(
        error,
        "We couldn't reach the server, but your registration has been queued.",
      );
    }
  },

  /** POST /auth/login */
  async login(email, password) {
    try {
      const { data } = await client.post("/auth/login", { email, password });
      return data;
    } catch (error) {
      return failure(
        error,
        "Unable to reach the server. Please try again in a moment.",
      );
    }
  },

  /** POST /auth/forgot-password */
  async forgotPassword(email) {
    try {
      const { data } = await client.post("/auth/forgot-password", { email });
      return data;
    } catch (error) {
      return failure(error, "Unable to reach the server right now.");
    }
  },

  /** GET /reviews — the approved customer reviews shown on the site. */
  async listReviews(limit = 20) {
    try {
      const { data } = await client.get("/reviews", { params: { limit } });
      return data;
    } catch (error) {
      return failure(error, "Reviews could not be loaded right now.");
    }
  },

  /** POST /reviews — a customer writes one; it waits to be approved. */
  async submitReview({ name, role, email, rating, title, body }) {
    try {
      const { data } = await client.post("/reviews", {
        name,
        role,
        email,
        rating,
        title,
        body,
      });
      return data;
    } catch (error) {
      return failure(error, "Your review could not be sent right now.");
    }
  },

  /** POST /auth/reset-password — the link from the reset email lands here. */
  async resetPassword({ email, token, password }) {
    try {
      const { data } = await client.post("/auth/reset-password", {
        email,
        token,
        password,
      });
      return data;
    } catch (error) {
      return failure(error, "That reset link is no longer valid.");
    }
  },

  /** POST /subscriptions/checkout */
  async checkout({ email, planId, provider }) {
    try {
      const { data } = await client.post("/subscriptions/checkout", {
        email,
        planId,
        provider,
      });
      return data;
    } catch (error) {
      return failure(
        error,
        "We couldn't reach the billing server, continuing in demo mode.",
      );
    }
  },

  /** POST /subscriptions/confirm — settle a payment on the way back from Stripe. */
  async confirmCheckout(sessionId) {
    try {
      const { data } = await client.post("/subscriptions/confirm", {
        sessionId,
      });
      return data;
    } catch (error) {
      return failure(
        error,
        "We couldn't confirm your payment. Please contact support.",
      );
    }
  },

  /** GET /subscriptions/status?email= */
  async getSubscriptionStatus(email) {
    try {
      const { data } = await client.get("/subscriptions/status", {
        params: { email },
      });
      return data;
    } catch (error) {
      return failure(
        error,
        "We couldn't check your status right now. Please try again later.",
      );
    }
  },

  /** POST /contact */
  async sendContact({ name, email, subject, message }) {
    try {
      const { data } = await client.post("/contact", {
        name,
        email,
        subject,
        message,
      });
      return data;
    } catch (error) {
      return failure(
        error,
        "Message queued — we'll get back to you as soon as we're back online.",
      );
    }
  },
};

export default api;
